import { useEffect, useRef, useState } from 'react';
import Modal, { ModalButton } from '../dm/Modal';
import Avatar from '../components/Avatar';
import { Icons } from '../dm/Menu';
import { useAuth } from '../AuthContext';
import { useServer, useServers } from './ServersContext';
import ServerIcon from './ServerIcon';
import { canDo, cleanInfo, inviteLink, orderedStructure, outranks } from './format';

const input = 'w-full rounded border border-solid border-border bg-surface px-3 py-2 font-body text-sm text-text outline-none placeholder:text-text-dim focus:border-purple';
const smallBtn = 'cursor-pointer rounded-pill border-0 bg-surface-3 px-3 py-1.5 font-display text-xs font-bold text-text hover:bg-border disabled:opacity-50';
const iconBtn = 'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-text-dim hover:bg-surface-3 hover:text-text disabled:cursor-default disabled:opacity-30';

// Server settings for owners and admins: overview (name, icon, description), channels, invites, ownership
export default function ServerSettings({ nodeId, onClose }) {
  const { st } = useServer(nodeId);
  const role = st.me?.role;
  const tabs = [
    canDo(role, 'manage_server') && ['overview', 'Overview'],
    canDo(role, 'manage_channels') && ['channels', 'Channels'],
    canDo(role, 'view_reports') && ['reports', st.openReports ? `Reports (${st.openReports})` : 'Reports'],
    canDo(role, 'ban_members') && ['bans', 'Bans'],
    canDo(role, 'manage_invites') && ['invites', 'Invites'],
    canDo(role, 'manage_server') && ['limits', 'Limits'],
    canDo(role, 'transfer_ownership') && ['ownership', 'Ownership'],
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs[0]?.[0]);

  return (
    <Modal title="Server settings" onClose={onClose} wide>
      <div className="-mx-1 mb-4 flex gap-1 overflow-x-auto pb-1" data-noswipe>
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`shrink-0 cursor-pointer rounded-pill border-0 px-3.5 py-1.5 font-display text-sm font-bold ${tab === id ? 'bg-brand text-white' : 'bg-transparent text-text-muted hover:bg-surface-3 hover:text-text'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="max-h-[60vh] min-h-[200px] overflow-y-auto overscroll-contain">
        {tab === 'overview' && <Overview nodeId={nodeId} />}
        {tab === 'channels' && <Channels nodeId={nodeId} />}
        {tab === 'invites' && <Invites nodeId={nodeId} />}
        {tab === 'reports' && <Reports nodeId={nodeId} />}
        {tab === 'bans' && <Bans nodeId={nodeId} />}
        {tab === 'limits' && <Limits nodeId={nodeId} />}
        {tab === 'ownership' && <Ownership nodeId={nodeId} onDone={onClose} />}
      </div>
    </Modal>
  );
}

function useNodeAction(nodeId) {
  const { conn } = useServers();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true); setError('');
    try { return await fn(conn(nodeId)); } catch (err) { setError(err.message || 'Something went wrong.'); return null; } finally { setBusy(false); }
  };
  return { busy, error, run };
}

/* ── Overview ── */

function Overview({ nodeId }) {
  const { node, st } = useServer(nodeId);
  const { update } = useServers();
  const [name, setName] = useState(st.info?.name || '');
  const [description, setDescription] = useState(st.info?.description || '');
  const [saved, setSaved] = useState(false);
  const fileRef = useRef();
  const { busy, error, run } = useNodeAction(nodeId);

  const apply = (info) => update(nodeId, s => ({ ...s, info: { ...cleanInfo(info), id: s.info?.id } }));

  const save = async () => {
    const res = await run(c => c.patch('/api/server', { name, description }));
    if (res) { apply(res); setSaved(true); setTimeout(() => setSaved(false), 1500); }
  };
  const uploadIcon = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await run(c => c.post('/api/server/icon', fd));
    if (res) apply(res);
  };
  const removeIcon = async () => {
    const res = await run(c => c.del('/api/server/icon'));
    if (res) apply(res);
  };

  const changed = name.trim() !== (st.info?.name || '') || (description.trim() || null) !== (st.info?.description || null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <ServerIcon node={node} icon={st.info?.icon} name={st.info?.name} size={72} className="rounded-xl" />
        <div className="flex flex-wrap gap-2">
          <button type="button" className={smallBtn} disabled={busy} onClick={() => fileRef.current?.click()}>Upload icon</button>
          {st.info?.icon && <button type="button" className={smallBtn} disabled={busy} onClick={removeIcon}>Remove</button>}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { uploadIcon(e.target.files[0]); e.target.value = ''; }} />
        </div>
      </div>
      <label className="text-xs font-bold text-text-dim">
        Server name
        <input value={name} maxLength={100} onChange={e => setName(e.target.value)} className={`${input} mt-1`} />
      </label>
      <label className="text-xs font-bold text-text-dim">
        Description
        <textarea value={description} maxLength={500} rows={3} onChange={e => setDescription(e.target.value)} placeholder="What is this server about?" className={`${input} mt-1 resize-none`} />
      </label>
      {error && <div className="text-xs text-red">{error}</div>}
      <div className="flex justify-end">
        <button type="button" disabled={busy || !changed || !name.trim()} onClick={save} className="cursor-pointer rounded-pill border-0 bg-brand px-5 py-2 font-display text-sm font-bold text-white disabled:opacity-50">
          {saved ? 'Saved' : busy ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/* ── Channels ── */

function Channels({ nodeId }) {
  const { st } = useServer(nodeId);
  const { busy, error, run } = useNodeAction(nodeId);
  const [newCategory, setNewCategory] = useState('');
  const [adding, setAdding] = useState(null); // category id (or 'none') with the "new channel" field open
  const [newChannel, setNewChannel] = useState('');
  const [editing, setEditing] = useState(null); // { type, id, name, topic }
  const { loose, groups } = orderedStructure(st.categories, st.channels);

  // Full layout in display order after moving one item
  const saveLayout = (cats, chans) => run(c => c.put('/api/layout', {
    categories: cats.map(x => x.id),
    channels: chans.map(x => ({ id: x.id, category_id: x.category_id })),
  }));
  const flat = () => [...loose.map(c => ({ ...c, category_id: null })), ...groups.flatMap(g => g.channels)];

  const moveCategory = (id, dir) => {
    const cats = groups.map(g => g.category);
    const i = cats.findIndex(c => c.id === id);
    const j = i + dir;
    if (j < 0 || j >= cats.length) return;
    [cats[i], cats[j]] = [cats[j], cats[i]];
    saveLayout(cats, flat());
  };
  const moveChannel = (channel, dir) => {
    const list = flat();
    const same = list.filter(c => (c.category_id || null) === (channel.category_id || null));
    const i = same.findIndex(c => c.id === channel.id);
    const j = i + dir;
    if (j < 0 || j >= same.length) return;
    [same[i], same[j]] = [same[j], same[i]];
    const rest = list.filter(c => (c.category_id || null) !== (channel.category_id || null));
    saveLayout(groups.map(g => g.category), [...same, ...rest]);
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    if (await run(c => c.post('/api/categories', { name: newCategory }))) setNewCategory('');
  };
  const addChannel = async () => {
    if (!newChannel.trim()) return;
    if (await run(c => c.post('/api/channels', { name: newChannel, category_id: adding === 'none' ? null : adding }))) {
      setNewChannel(''); setAdding(null);
    }
  };
  const saveEdit = async () => {
    const ok = editing.type === 'category'
      ? await run(c => c.patch(`/api/categories/${editing.id}`, { name: editing.name }))
      : await run(c => c.patch(`/api/channels/${editing.id}`, { name: editing.name, topic: editing.topic }));
    if (ok) setEditing(null);
  };
  const remove = async (type, item) => {
    const what = type === 'category' ? `the category "${item.name}" (its channels stay)` : `#${item.name} and all its messages`;
    if (!window.confirm(`Delete ${what}?`)) return;
    await run(c => c.del(`/api/${type === 'category' ? 'categories' : 'channels'}/${item.id}`));
  };

  const channelLine = (ch, list) => {
    const idx = list.findIndex(c => c.id === ch.id);
    if (editing?.type === 'channel' && editing.id === ch.id) {
      return (
        <div key={ch.id} className="flex flex-col gap-1.5 rounded bg-surface px-2 py-2">
          <input autoFocus value={editing.name} maxLength={50} onChange={e => setEditing({ ...editing, name: e.target.value })} className={input} placeholder="channel-name" />
          <input value={editing.topic || ''} maxLength={300} onChange={e => setEditing({ ...editing, topic: e.target.value })} className={input} placeholder="Topic (optional)" />
          <div className="flex justify-end gap-2">
            <button type="button" className={smallBtn} onClick={() => setEditing(null)}>Cancel</button>
            <button type="button" className={smallBtn} disabled={busy} onClick={saveEdit}>Save</button>
          </div>
        </div>
      );
    }
    return (
      <div key={ch.id} className="flex items-center gap-1 rounded-sm px-2 py-1 hover:bg-surface">
        <span className="text-text-dim">#</span>
        <span className="min-w-0 flex-1 truncate text-sm text-text">{ch.name}</span>
        <button type="button" className={iconBtn} aria-label="Move up" disabled={busy || idx === 0} onClick={() => moveChannel(ch, -1)}>▲</button>
        <button type="button" className={iconBtn} aria-label="Move down" disabled={busy || idx === list.length - 1} onClick={() => moveChannel(ch, 1)}>▼</button>
        <button type="button" className={iconBtn} aria-label="Edit channel" onClick={() => setEditing({ type: 'channel', id: ch.id, name: ch.name, topic: ch.topic || '' })}>{Icons.edit}</button>
        <button type="button" className={iconBtn} aria-label="Delete channel" disabled={busy || st.channels.length <= 1} onClick={() => remove('channel', ch)}>{Icons.trash}</button>
      </div>
    );
  };

  const addChannelRow = (key) => (adding === key ? (
    <div className="mt-1 flex gap-2 px-2">
      <input autoFocus value={newChannel} maxLength={50} onChange={e => setNewChannel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addChannel(); if (e.key === 'Escape') setAdding(null); }} placeholder="new-channel" className={input} />
      <button type="button" className={smallBtn} disabled={busy || !newChannel.trim()} onClick={addChannel}>Add</button>
    </div>
  ) : (
    <button type="button" onClick={() => { setAdding(key); setNewChannel(''); }} className="ml-2 mt-0.5 cursor-pointer border-0 bg-transparent p-1 text-xs font-semibold text-purple-light hover:underline">+ Add channel</button>
  ));

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-text-dim">{st.channels.length} of {st.info?.settings.max_channels} channels</div>
      {(loose.length > 0 || groups.length === 0) && (
        <div>
          {loose.map(ch => channelLine(ch, loose))}
          {addChannelRow('none')}
        </div>
      )}
      {groups.map(({ category, channels }, gi) => (
        <div key={category.id} className="rounded bg-surface-3 px-1 py-1.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
          {editing?.type === 'category' && editing.id === category.id ? (
            <div className="flex gap-2 px-1 pb-1">
              <input autoFocus value={editing.name} maxLength={50} onChange={e => setEditing({ ...editing, name: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} className={input} />
              <button type="button" className={smallBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className={smallBtn} disabled={busy} onClick={saveEdit}>Save</button>
            </div>
          ) : (
            <div className="flex items-center gap-1 px-2 pb-1">
              <span className="min-w-0 flex-1 truncate text-[11px] font-extrabold uppercase tracking-wider text-text-muted">{category.name}</span>
              <button type="button" className={iconBtn} aria-label="Move category up" disabled={busy || gi === 0} onClick={() => moveCategory(category.id, -1)}>▲</button>
              <button type="button" className={iconBtn} aria-label="Move category down" disabled={busy || gi === groups.length - 1} onClick={() => moveCategory(category.id, 1)}>▼</button>
              <button type="button" className={iconBtn} aria-label="Rename category" onClick={() => setEditing({ type: 'category', id: category.id, name: category.name })}>{Icons.edit}</button>
              <button type="button" className={iconBtn} aria-label="Delete category" disabled={busy} onClick={() => remove('category', category)}>{Icons.trash}</button>
            </div>
          )}
          {channels.map(ch => channelLine(ch, channels))}
          {addChannelRow(category.id)}
        </div>
      ))}
      <div className="flex gap-2">
        <input value={newCategory} maxLength={50} onChange={e => setNewCategory(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCategory(); }} placeholder="New category" className={input} />
        <button type="button" className={smallBtn} disabled={busy || !newCategory.trim()} onClick={addCategory}>Add</button>
      </div>
      {error && <div className="text-xs text-red">{error}</div>}
    </div>
  );
}

/* ── Invites ── */

function Invites({ nodeId }) {
  const { busy, error, run } = useNodeAction(nodeId);
  const [list, setList] = useState(null);

  useEffect(() => {
    run(c => c.get('/api/invites')).then(r => setList(Array.isArray(r) ? r : []));
  }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (code) => {
    if (await run(c => c.del(`/api/invites/${encodeURIComponent(code)}`))) setList(prev => prev.filter(i => i.code !== code));
  };

  if (!list) return <div className="py-4 text-sm text-text-dim">Loading…</div>;
  if (!list.length) return <div className="py-4 text-sm text-text-dim">No active invites.</div>;
  return (
    <div className="flex flex-col gap-1.5">
      {list.filter(i => /^[A-Za-z0-9]{6,16}$/.test(i.code)).map(i => (
        <div key={i.code} className="flex items-center gap-3 rounded bg-surface px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-xs text-text">{inviteLink(nodeId, i.code)}</div>
            <div className="truncate text-[11px] text-text-dim">
              by {i.creator ? `@${String(i.creator.username).slice(0, 50)}` : 'Deleted user'} · {i.uses}{i.max_uses ? `/${i.max_uses}` : ''} uses · {i.expires_at ? `expires ${new Date(i.expires_at).toLocaleString()}` : 'never expires'}
            </div>
          </div>
          <button type="button" className={smallBtn} disabled={busy} onClick={() => revoke(i.code)}>Revoke</button>
        </div>
      ))}
      {error && <div className="text-xs text-red">{error}</div>}
    </div>
  );
}

/* ── Ownership ── */

function Ownership({ nodeId, onDone }) {
  const { user } = useAuth();
  const { st } = useServer(nodeId);
  const { ensureMembers } = useServers();
  const { busy, error, run } = useNodeAction(nodeId);
  const [target, setTarget] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => { ensureMembers(nodeId); }, [nodeId, ensureMembers]);
  const candidates = (st.members || [])
    .filter(m => m.id !== user?.id)
    .filter(m => !query.trim() || `${m.username} ${m.display_name || ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 50);

  const transfer = async () => {
    if (await run(c => c.post('/api/server/owner', { user_id: target.id }))) onDone();
  };

  if (target) {
    return (
      <div className="flex flex-col gap-3 text-sm leading-relaxed text-text-muted">
        <div>
          Make <strong className="text-text">{target.display_name || target.username}</strong> the owner of this server?
          You become an admin. Only the new owner can give ownership back.
        </div>
        <div className="text-xs text-text-dim">You stay the host of the node itself (re-key and moving it).</div>
        {error && <div className="text-xs text-red">{error}</div>}
        <div className="flex gap-2">
          <ModalButton onClick={() => setTarget(null)}>Cancel</ModalButton>
          <ModalButton danger disabled={busy} onClick={transfer}>{busy ? '…' : 'Transfer ownership'}</ModalButton>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm text-text-muted">The owner can do everything on this server. Choose a member to hand it over to.</div>
      <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search members" className={input} />
      {!st.members && <div className="py-2 text-sm text-text-dim">Loading…</div>}
      {st.members && candidates.length === 0 && <div className="py-2 text-sm text-text-dim">No other members yet.</div>}
      {candidates.map(m => (
        <button key={m.id} type="button" onClick={() => setTarget(m)} className="flex cursor-pointer items-center gap-3 rounded border-0 bg-transparent px-2 py-1.5 text-left hover:bg-surface">
          <Avatar src={m.avatar} size={30} border="none" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-display text-sm font-bold text-text">{m.display_name || m.username}</span>
            <span className="block truncate text-xs text-text-dim">@{m.username}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/* ── Reports (moderation panel) ── */

const TIMEOUT_CHOICES = [[10, '10 minutes'], [60, '1 hour'], [1440, '1 day'], [10080, '1 week']];
const CATEGORY_LABEL = { spam: 'Spam', harassment: 'Harassment', other: 'Other' };

function Reports({ nodeId }) {
  const { st } = useServer(nodeId);
  const { busy, error, run } = useNodeAction(nodeId);
  const [status, setStatus] = useState('open');
  const [list, setList] = useState(null);

  const load = () => run(c => c.get(`/api/reports?status=${status}`)).then(r => setList(Array.isArray(r) ? r : []));
  useEffect(() => { setList(null); load(); }, [nodeId, status, st.openReports]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {['open', 'closed'].map(v => (
          <button key={v} type="button" onClick={() => setStatus(v)} className={`${smallBtn} ${status === v ? '' : 'bg-transparent text-text-muted'}`}>{v === 'open' ? 'Open' : 'Closed'}</button>
        ))}
      </div>
      <div className="text-xs text-text-dim">Reports from members of this server. They don't go to BLOBL.</div>
      {!list && <div className="py-4 text-sm text-text-dim">Loading…</div>}
      {list && list.length === 0 && <div className="py-4 text-sm text-text-dim">{status === 'open' ? 'No open reports.' : 'No closed reports yet.'}</div>}
      {list && list.map(r => <ReportCard key={r.id} nodeId={nodeId} report={r} busy={busy} run={run} onChanged={load} />)}
      {error && <div className="text-xs text-red">{error}</div>}
    </div>
  );
}

const nameOf = (u) => (u ? String(u.display_name || u.username || '').slice(0, 100) || 'unknown' : 'Deleted user');

function ReportCard({ nodeId, report, busy, run, onChanged }) {
  const { st } = useServer(nodeId);
  const role = st.me?.role;
  const [minutes, setMinutes] = useState(60);
  const target = report.target;
  const canAct = target && outranks(role, target.role || 'member') && target.role;
  const snapshot = Array.isArray(report.snapshot) ? report.snapshot.slice(0, 11) : [];
  const act = async (fn) => { if (await run(fn)) onChanged(); };

  return (
    <div className="rounded bg-surface px-3 py-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-dim">
        {report.category && <span className="rounded-sm bg-red px-1.5 py-0.5 font-bold uppercase text-white">{CATEGORY_LABEL[report.category] || 'Other'}</span>}
        {report.channel && <span>#{String(report.channel.name).slice(0, 50)}</span>}
        <span>{new Date(report.created_at).toLocaleString()}</span>
        <span>by {nameOf(report.reporter)}</span>
      </div>
      {report.reason && <div className="mt-1.5 whitespace-pre-wrap break-words text-sm text-text">{String(report.reason).slice(0, 500)}</div>}
      <div className="mt-2 flex flex-col gap-1 rounded-sm bg-surface-2 p-2">
        {snapshot.map(m => (
          <div key={m.id} className={`rounded-sm px-2 py-1 text-xs `} style={m.id === report.message_id ? { background: 'rgba(244,63,94,0.15)' } : undefined}>
            <span className="font-bold text-text-muted">{nameOf(m.author)}</span>
            <span className="ml-1.5 text-text-dim">{new Date(m.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
            <div className="whitespace-pre-wrap break-words text-text">{String(m.content || '').slice(0, 4000)}</div>
            {Array.isArray(m.files) && m.files.length > 0 && (
              <div className="text-text-dim">{m.files.length} {m.files.length === 1 ? 'file' : 'files'}</div>
            )}
          </div>
        ))}
      </div>
      {report.status === 'open' ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {report.message_exists && canDo(role, 'delete_messages') && (
            <button type="button" className={`${smallBtn} text-red`} disabled={busy} onClick={() => act(c => c.del(`/api/messages/${report.message_id}`))}>Delete message</button>
          )}
          {canAct && canDo(role, 'timeout_members') && (
            <span className="flex items-center gap-1">
              <select value={minutes} onChange={e => setMinutes(Number(e.target.value))} className="cursor-pointer rounded-pill border border-solid border-border bg-surface-2 px-2 py-1 text-xs text-text outline-none">
                {TIMEOUT_CHOICES.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
              </select>
              <button type="button" className={`${smallBtn} text-amber`} disabled={busy} onClick={() => act(c => c.post(`/api/members/${target.id}/timeout`, { minutes, reason: `Report #${report.id}` }))}>Timeout</button>
            </span>
          )}
          {canAct && canDo(role, 'kick_members') && (
            <button type="button" className={`${smallBtn} text-red`} disabled={busy} onClick={() => { if (window.confirm(`Kick ${nameOf(target)}?`)) act(c => c.del(`/api/members/${target.id}`)); }}>Kick</button>
          )}
          {target && (canAct || !target.role) && canDo(role, 'ban_members') && (
            <button type="button" className={`${smallBtn} text-red`} disabled={busy} onClick={() => { if (window.confirm(`Ban ${nameOf(target)}?`)) act(c => c.post('/api/bans', { user_id: target.id, reason: `Report #${report.id}` })); }}>Ban</button>
          )}
          <button type="button" className={`${smallBtn} ml-auto`} disabled={busy} onClick={() => act(c => c.post(`/api/reports/${report.id}/close`))}>Close report</button>
        </div>
      ) : (
        <div className="mt-2 text-xs text-text-dim">Closed by {report.handled_by || 'Deleted user'}{report.handled_at ? ` on ${new Date(report.handled_at).toLocaleString()}` : ''}</div>
      )}
    </div>
  );
}

/* ── Bans ── */

function Bans({ nodeId }) {
  const { busy, error, run } = useNodeAction(nodeId);
  const [list, setList] = useState(null);
  useEffect(() => { run(c => c.get('/api/bans')).then(r => setList(Array.isArray(r) ? r : [])); }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unban = async (id) => {
    if (await run(c => c.del(`/api/bans/${id}`))) setList(prev => prev.filter(b => b.user.id !== id));
  };
  if (!list) return <div className="py-4 text-sm text-text-dim">Loading…</div>;
  if (!list.length) return <div className="py-4 text-sm text-text-dim">Nobody is banned.</div>;
  return (
    <div className="flex flex-col gap-1.5">
      {list.map(b => (
        <div key={b.user.id} className="flex items-center gap-3 rounded bg-surface px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-sm font-bold text-text">{nameOf(b.user)} <span className="font-body text-xs font-normal text-text-dim">@{String(b.user.username).slice(0, 50)}</span></div>
            <div className="truncate text-[11px] text-text-dim">{b.reason ? String(b.reason).slice(0, 500) : 'No reason'} · by {b.banned_by ? String(b.banned_by).slice(0, 50) : 'Deleted user'}</div>
          </div>
          <button type="button" className={smallBtn} disabled={busy} onClick={() => unban(b.user.id)}>Unban</button>
        </div>
      ))}
      {error && <div className="text-xs text-red">{error}</div>}
    </div>
  );
}

/* ── Limits ── */

function Limits({ nodeId }) {
  const { st } = useServer(nodeId);
  const { update } = useServers();
  const { busy, error, run } = useNodeAction(nodeId);
  const s0 = st.info?.settings || {};
  const [form, setForm] = useState({
    members_can_invite: s0.members_can_invite !== false,
    max_channels: s0.max_channels || 50,
    max_members: s0.max_members || '',
    max_upload_mb: s0.max_upload_mb || 25,
  });
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const save = async () => {
    const res = await run(c => c.patch('/api/server/settings', {
      members_can_invite: form.members_can_invite,
      max_channels: Number(form.max_channels),
      max_members: form.max_members === '' ? null : Number(form.max_members),
      max_upload_mb: Number(form.max_upload_mb),
    }));
    if (res) { update(nodeId, x => ({ ...x, info: { ...cleanInfo(res), id: x.info?.id } })); setSaved(true); }
  };

  return (
    <div className="flex flex-col gap-4">
      <label className="flex cursor-pointer items-center gap-3 text-sm text-text">
        <input type="checkbox" checked={form.members_can_invite} onChange={e => set('members_can_invite', e.target.checked)} className="accent-[#9b5de5]" />
        Members can create invite links
      </label>
      <label className="text-xs font-bold text-text-dim">
        Max file size (MB)
        <input type="number" min={1} max={s0.upload_cap_mb} value={form.max_upload_mb} onChange={e => set('max_upload_mb', e.target.value)} className={`${input} mt-1`} />
        <span className="mt-1 block font-normal">The host allows at most {s0.upload_cap_mb} MB (MAX_UPLOAD_MB_CAP).</span>
      </label>
      <label className="text-xs font-bold text-text-dim">
        Max channels
        <input type="number" min={1} max={500} value={form.max_channels} onChange={e => set('max_channels', e.target.value)} className={`${input} mt-1`} />
      </label>
      <label className="text-xs font-bold text-text-dim">
        Max members
        <input type="number" min={2} value={form.max_members} placeholder="No limit" onChange={e => set('max_members', e.target.value)} className={`${input} mt-1`} />
      </label>
      {error && <div className="text-xs text-red">{error}</div>}
      <div className="flex justify-end">
        <button type="button" disabled={busy} onClick={save} className="cursor-pointer rounded-pill border-0 bg-brand px-5 py-2 font-display text-sm font-bold text-white disabled:opacity-50">
          {saved ? 'Saved' : busy ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
